'use client';

import 'swiper/css';
import 'swiper/css/free-mode';
import 'swiper/css/pagination';

import type { PackagePricingPlansProps } from '@/core/types';

import React, { useState } from 'react';
import { FreeMode } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import { Box, Button, Container, Typography } from '@/components/ui';
import { PackageCard, BundlesDurations } from '@/components/bundles';

import { RouterLink } from '@/routes/components';

export default function PackagePricingPlans({
  title,
  subtitle,
  durations = [],
  button,
  packages = [],
  className = '',
}: PackagePricingPlansProps) {
  const [activeDuration, setActiveDuration] = useState(durations.length > 0 ? durations[0].id : '');

  const handleDurationChange = (durationId: string) => {
    setActiveDuration(durationId);
  };

  const renderDurations = () => (
    <BundlesDurations
      durations={durations}
      activeDuration={activeDuration}
      handleDurationChange={handleDurationChange}
    />
  );

  const renderPackageCard = (pac: any, indx: number) => {
    const currentPackage =
      pac?.tariffs?.find((price: any) => price.id === activeDuration) || pac?.tariffs?.[0];

    return <PackageCard key={indx} package={pac} currentPackage={currentPackage} />;
  };

  const renderPackages = () => (
    <Box className="mt-2 md:mt-10">
      <Swiper
        spaceBetween={16}
        modules={[FreeMode]}
        slidesPerView={1.1}
        breakpoints={{
          md: {
            slidesPerView: 2,
            enabled: false,
          },
        }}
        className="!overflow-visible lg:!overflow-hidden"
        wrapperClass="md:!grid grid-cols-2 lg:grid-cols-3 md:gap-6 md:!transform-none md:pb-[84px]"
      >
        {packages.map((pac, indx) => (
          <SwiperSlide key={indx} className="md:!w-full !h-auto">
            {renderPackageCard(pac, indx)}
          </SwiperSlide>
        ))}
      </Swiper>
    </Box>
  );

  return (
    <Box className={`bg-light-gray/56 pt-[6px] ${className} `}>
      <Container className="overflow-hidden">
        <Box
          className={`flex flex-col xl:flex-row gap-8 mb-5 justify-between md:border-b md:pb-12 ${
            subtitle ? 'items-center xl:items-end' : 'items-center'
          }`}
        >
          <Box className="max-w-[826px] flex-grow">
            {title && (
              <Typography
                variant="h2"
                className="text-center xl:text-left text-2xl md:text-[32px] font-bold uppercase"
              >
                {title}
              </Typography>
            )}

            {subtitle && (
              <Typography
                variant="body1"
                className="text-sm md:text-lg text-black font-semibold max-w-2xl mx-auto mt-4 md:mt-[26px] text-center xl:text-left"
              >
                {subtitle}
              </Typography>
            )}
          </Box>
          {durations.length > 0 && renderDurations()}
        </Box>

        {renderPackages()}

        {button && (
          <Box className="flex justify-center gap-4 mt-8">
            <Button
              component={RouterLink}
              href="/seller/billing/tariffs"
              variant="outlined"
              color="primary"
              className="min-w-[171px] md:min-w-[180px] px-2 md:px-4 text-sm md:text-base"
            >
              {button.text}
            </Button>
          </Box>
        )}
      </Container>
    </Box>
  );
}
